import { faCreditCard, faInfoCircle, faTrash, faTrashRestore } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Tab, Tabs } from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import CreditCardDetails from '../../../components/CreditCards/CreditCardDetails';
import PageBreadcrumb from '../../../components/PageBreadcrumb';
import PageContainer from '../../../components/PageContainer';
import Customer from '../../../entities/Customer/Customer';
import CustomerService from '../../../services/customer/CustomerService';
import CustomerAudit from '../Tabs/CustomerAudit';
import useCustomerHomeStyles from './CustomerHomeStyles';

type ParamTypes = {
  customerId: string
}

type ConfirmAction = 'delete' | 'restore' | null;

const CustomerHome = () => {
  const [t] = useTranslation();
  const { customerId } = useParams<ParamTypes>();
  const styles = useCustomerHomeStyles();
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [loading, setLoading] = useState(true);
  const [currentTab, setCurrentTab] = useState(0);
  const [confirmAction, setConfirmAction] = useState<ConfirmAction>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const loadCustomer = useCallback(() => {
    if (!customerId) return;
    setLoading(true);
    CustomerService.getCustomer(customerId, true)
      .then((response: Customer) => setCustomer(response))
      .finally(() => setLoading(false));
  }, [customerId]);

  useEffect(() => {
    loadCustomer();
  }, [loadCustomer]);

  const isDeleted = !!customer?.deletedAt;

  const handleConfirm = async () => {
    if (!customerId || !confirmAction) return;
    setLoading(true);
    setActionError(null);
    try {
      if (confirmAction === 'delete') {
        await CustomerService.softDeleteCustomer(customerId);
      } else {
        await CustomerService.restoreCustomer(customerId);
      }
      setConfirmAction(null);
      loadCustomer();
    } catch {
      setActionError(
        confirmAction === 'delete'
          ? t('customers.home.delete.error')
          : t('customers.home.restore.error'),
      );
      setConfirmAction(null);
      setLoading(false);
    }
  };

  const renderCurrentTab = () => {
    switch (currentTab) {
      case 0:
        if (customer) {
          return <CreditCardDetails customer={customer} setLoading={setLoading} />;
        }
        break;
      case 1:
        if (customer) {
          return <CustomerAudit customer={customer} setLoading={setLoading} />;
        }
        break;
      default:
        return (<span>-1</span>);
    }
    return null;
  };

  const dialogTitle = confirmAction === 'delete'
    ? t('customers.home.delete.confirm_title')
    : t('customers.home.restore.confirm_title');
  const dialogText = confirmAction === 'delete'
    ? t('customers.home.delete.confirm_text', { name: customer?.name })
    : t('customers.home.restore.confirm_text', { name: customer?.name });
  const dialogConfirmLabel = confirmAction === 'delete'
    ? t('customers.home.delete.confirm')
    : t('customers.home.restore.confirm');
  const dialogCancelLabel = confirmAction === 'delete'
    ? t('customers.home.delete.cancel')
    : t('customers.home.restore.cancel');

  return (
    <PageContainer loading={loading}>
      <PageBreadcrumb items={[t('customers.list.title'), (customer?.name || '')]} />

      {customer && isDeleted && (
        <Alert severity="warning" style={{ marginBottom: 16 }}>
          {t('customers.home.restore.deleted_banner', { date: new Date(customer.deletedAt!).toLocaleString() })}
        </Alert>
      )}

      {actionError && (
        <Alert severity="error" style={{ marginBottom: 16 }} onClose={() => setActionError(null)}>
          {actionError}
        </Alert>
      )}

      {customer && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
          {isDeleted ? (
            <Button
              variant="contained"
              color="primary"
              startIcon={<FontAwesomeIcon icon={faTrashRestore} />}
              onClick={() => setConfirmAction('restore')}
            >
              {t('customers.home.restore.button')}
            </Button>
          ) : (
            <Button
              variant="outlined"
              style={{ color: '#d32f2f', borderColor: '#d32f2f' }}
              startIcon={<FontAwesomeIcon icon={faTrash} />}
              onClick={() => setConfirmAction('delete')}
            >
              {t('customers.home.delete.button')}
            </Button>
          )}
        </div>
      )}

      <Tabs
        value={currentTab}
        onChange={(_, newTab) => setCurrentTab(newTab)}
        indicatorColor="secondary"
        textColor="primary"
        aria-label="scrollable auto tabs example"
        className={styles.tabs}
      >
        <Tab icon={<FontAwesomeIcon icon={faCreditCard} />} label={t('customers.home.tabs.credit_cards')} />
        <Tab icon={<FontAwesomeIcon icon={faInfoCircle} />} label={t('customers.home.tabs.audit')} />
      </Tabs>

      <div id="customer" className={styles.homeCustomerContainer}>
        {renderCurrentTab()}
      </div>

      <Dialog
        open={confirmAction !== null}
        onClose={() => setConfirmAction(null)}
        aria-labelledby="customer-action-dialog-title"
        aria-describedby="customer-action-dialog-description"
      >
        <DialogTitle id="customer-action-dialog-title">{dialogTitle}</DialogTitle>
        <DialogContent>
          <DialogContentText id="customer-action-dialog-description">
            {dialogText}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmAction(null)} color="primary">
            {dialogCancelLabel}
          </Button>
          <Button onClick={handleConfirm} color="primary" autoFocus>
            {dialogConfirmLabel}
          </Button>
        </DialogActions>
      </Dialog>
    </PageContainer>
  );
};

export default CustomerHome;
